import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { cubeLogo, serviceCategories } from "../data/mock";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  Menu,
  ChevronDown,
  ArrowUpRight,
  Search,
  X,
  Megaphone,
  Video,
  Newspaper,
  Globe,
  Zap,
  // Sub-service icons
  Share2,
  Target,
  Users,
  MessageCircle,
  MapPin,
  Settings,
  Camera,
  Film,
  Scissors,
  User,
  FileText,
  Mic,
  Tv,
  Palette,
  Code,
  Smartphone,
  ShoppingCart,
  Briefcase,
  Bot,
  Lightbulb,
  TrendingUp,
  Mail,
  Linkedin,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Category icon mapping
const categoryIconMap = {
  "digital-marketing": Megaphone,
  "content-creation": Video,
  "publicity-pr": Newspaper,
  "web-design-it": Globe,
  "trending": Zap,
};

// Sub-service icon mapping - customized for each sub-service
const subServiceIconMap = {
  // Digital Marketing
  "social-media-management": Share2,
  "performance-marketing": Target,
  "influencer-marketing": Users,
  "whatsapp-api-marketing": MessageCircle,
  "google-my-business": MapPin,
  "marketing-automation": Settings,
  // Content Creation
  "reels-production": Film,
  "photography-videography": Camera,
  "video-editing": Scissors,
  "personal-branding": User,
  "copywriting-content": FileText,
  // Publicity & PR
  "personal-branding-pr": User,
  "media-news-coverage": Newspaper,
  "podcast-production": Mic,
  "tv-production": Tv,
  // Web Design & IT
  "ui-ux-design": Palette,
  "website-development": Code,
  "app-development": Smartphone,
  "ecommerce-solutions": ShoppingCart,
  "business-automation": Briefcase,
  // Trending
  "ai-tools-automation": Bot,
  "lead-generation": Lightbulb,
  "personal-brand-growth": TrendingUp,
  "email-marketing": Mail,
  "linkedin-outreach": Linkedin,
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const megaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setActiveMega(null);
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) {
        setActiveMega(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || activeMega
          ? "bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
      ref={megaRef}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Larger size */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src={cubeLogo}
              alt="Cube Multimedia"
              className="h-16 lg:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav - Service Categories */}
          <nav className="hidden lg:flex items-center gap-1">
            {serviceCategories.map((cat) => (
              <div key={cat.id} className="relative">
                <button
                  onMouseEnter={() => setActiveMega(cat.id)}
                  onClick={() => {
                    if (activeMega === cat.id) {
                      setActiveMega(null);
                      navigate(`/services/${cat.id}`);
                    } else {
                      setActiveMega(cat.id);
                    }
                  }}
                  className={`px-3 py-2 text-[13px] font-medium flex items-center gap-1.5 rounded-lg transition-colors duration-200 ${
                    activeMega === cat.id || location.pathname === `/services/${cat.id}`
                      ? "text-white bg-white/[0.06]"
                      : "text-[#a1a1aa] hover:text-white"
                  }`}
                >
                  {cat.title}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      activeMega === cat.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={() => navigate("/gws-whatsapp-community")}
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-300 flex items-center gap-2"
            >
              Join Community
              <ArrowUpRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button className="p-2 text-white hover:text-[#dc2626] transition-colors duration-300">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0a0a0a] border-l border-white/[0.06] w-[320px] p-0 overflow-y-auto"
            >
              <div className="flex flex-col h-full p-6 pt-16">
                <nav className="flex flex-col gap-1">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 text-lg text-white hover:bg-white/[0.04] rounded-lg transition-colors"
                  >
                    Home
                  </Link>
                  {serviceCategories.map((cat) => (
                    <div key={cat.id}>
                      <button
                        onClick={() =>
                          setMobileExpanded(
                            mobileExpanded === cat.id ? null : cat.id
                          )
                        }
                        className="w-full px-4 py-3 text-lg text-[#a1a1aa] hover:text-white flex items-center justify-between hover:bg-white/[0.04] rounded-lg transition-colors"
                      >
                        {cat.title}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            mobileExpanded === cat.id ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {mobileExpanded === cat.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-6 pb-2">
                              <Link
                                to={`/services/${cat.id}`}
                                onClick={() => setMobileOpen(false)}
                                className="block px-4 py-2 text-sm text-[#dc2626] font-medium hover:bg-white/[0.04] rounded-lg transition-colors"
                              >
                                View All {cat.title}
                              </Link>
                              {cat.subServices.map((sub) => (
                                <Link
                                  key={sub.id}
                                  to={`/services/${cat.id}#${sub.id}`}
                                  onClick={() => setMobileOpen(false)}
                                  className="block px-4 py-2 text-sm text-[#71717a] hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors"
                                >
                                  {sub.title}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </nav>
                <div className="mt-auto pt-6 border-t border-white/[0.06]">
                  <Button
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/gws-whatsapp-community");
                    }}
                    className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-3 rounded-lg text-sm font-medium transition-colors duration-300"
                  >
                    Join Community
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mega Menu Dropdown */}
      <AnimatePresence>
        {activeMega && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            onMouseLeave={() => setActiveMega(null)}
            className="hidden lg:block absolute top-full left-0 right-0 bg-[#0f0f0f] border-b border-white/[0.06] shadow-2xl shadow-black/60"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
              {serviceCategories
                .filter((c) => c.id === activeMega)
                .map((cat) => {
                  const CategoryIcon = categoryIconMap[cat.id] || Zap;
                  return (
                  <div key={cat.id}>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-4">
                        {/* Category icon using lucide */}
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
                          <CategoryIcon className="w-6 h-6" style={{ color: cat.color }} />
                        </div>
                        <div>
                          <h3 className="text-white text-lg font-semibold">
                            {cat.title}
                          </h3>
                          <p className="text-[#71717a] text-sm">
                            {cat.shortDesc}
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/services/${cat.id}`}
                        className="text-[#dc2626] text-sm font-medium flex items-center gap-1 hover:underline"
                      >
                        View All
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {cat.subServices.map((sub) => {
                        const SubIcon = subServiceIconMap[sub.id] || CategoryIcon;
                        return (
                        <Link
                          key={sub.id}
                          to={`/services/${cat.id}#${sub.id}`}
                          className="group flex gap-4 p-4 rounded-xl hover:bg-white/[0.04] transition-colors duration-200"
                        >
                          <div className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: `${cat.color}10` }}>
                            <SubIcon className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-opacity" style={{ color: cat.color }} />
                          </div>
                          <div>
                            <h4 className="text-white text-sm font-medium group-hover:text-[#dc2626] transition-colors duration-200">
                              {sub.title}
                            </h4>
                            <p className="text-[#52525b] text-xs mt-1 line-clamp-2">
                              {sub.description.substring(0, 80)}...
                            </p>
                          </div>
                        </Link>
                      )})}
                    </div>
                  </div>
                )})}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
